import { useEffect, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDay = (d) => {
  const dt = new Date(d + "T12:00:00");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const Tile = ({ label, value, testid }) => (
  <div className="border border-line bg-surface p-5 text-center" data-testid={testid}>
    <p className="font-display text-3xl text-gold" data-testid={`${testid}-value`}>
      {value}
    </p>
    <p className="mt-2 font-jmono text-[9px] uppercase tracking-[0.3em] text-slate-500">{label}</p>
  </div>
);

const List = ({ title, items, testid }) => (
  <div className="border border-line bg-surface p-6" data-testid={testid}>
    <h3 className="font-display text-sm tracking-[0.2em] text-silver">{title}</h3>
    <div className="mt-5 space-y-3">
      {items.length === 0 && <p className="text-sm text-slate-600">Nothing yet.</p>}
      {items.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="min-w-0 truncate text-sm font-light text-slate-300">{r.label}</span>
          <span className="font-jmono text-[11px] text-gold">{r.count}</span>
        </div>
      ))}
    </div>
  </div>
);

const AdminInsights = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("nox_admin_token") || "";
    fetch(`${API}/api/admin/insights`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        setData(await res.json());
      })
      .catch(() => setError("Could not load the ledger of visitors."));
  }, []);

  if (error) {
    return (
      <div className="mt-16 border border-line bg-surface p-10 text-center">
        <p className="font-cormorant text-lg italic text-slate-500">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-16 flex flex-col items-center gap-5 py-16">
        <MoonMark className="h-8 w-8 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <p className="font-jmono text-[10px] uppercase tracking-[0.35em] text-slate-500">Reading the night&apos;s ledger…</p>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.count));

  return (
    <div className="mt-10" data-testid="admin-insights">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <Tile label="Total views" value={data.total_views} testid="insights-total" />
        <Tile label="Unique visitors" value={data.unique_visitors} testid="insights-unique" />
        <Tile label="Last 7 days" value={data.last_7_days} testid="insights-week" />
      </div>

      <div className="mt-8 border border-line bg-surface p-6" data-testid="insights-daily">
        <h3 className="font-display text-sm tracking-[0.2em] text-silver">Views · last 14 days</h3>
        <div className="mt-6 flex h-28 items-end justify-center gap-2">
          {data.daily.length === 0 && <p className="text-sm text-slate-600">No views recorded yet.</p>}
          {data.daily.map((d) => (
            <div key={d.day} className="flex h-full w-10 flex-col items-center justify-end gap-2">
              <div
                className="w-full rounded-t-sm bg-gradient-to-t from-violet-900/60 to-violet-400/80"
                style={{ height: `${Math.max(6, Math.round((d.count / maxDaily) * 86))}px` }}
                title={`${d.day}: ${d.count}`}
                data-testid={`insights-bar-${d.day}`}
              />
              <span className="font-jmono text-[8px] text-slate-600">{fmtDay(d.day)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <List title="Where they land" items={data.top_paths} testid="insights-paths" />
        <List title="How they arrived" items={data.top_referrers} testid="insights-referrers" />
        <List title="From where (timezones)" items={data.places} testid="insights-places" />
        <List title="Devices" items={data.devices} testid="insights-devices" />
      </div>

      <div className="mt-8 border border-line bg-surface p-6" data-testid="insights-recent">
        <h3 className="font-display text-sm tracking-[0.2em] text-silver">Recent visits</h3>
        <div className="mt-5 space-y-2.5">
          {data.recent.length === 0 && <p className="text-sm text-slate-600">The night is quiet — no visits yet.</p>}
          {data.recent.map((v, i) => (
            <div
              key={i}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-line/60 pb-2 text-[13px] last:border-0"
              data-testid={`insights-recent-${i}`}
            >
              <span className="font-jmono text-[11px] text-slate-500">
                {v.created_at ? new Date(v.created_at).toLocaleString() : ""}
              </span>
              <span className="text-slate-300">{v.path}</span>
              <span className="font-jmono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {v.timezone || "Unknown place"} · {v.device}
              </span>
              <span className="font-jmono text-[10px] text-slate-600">{v.referrer ? `via ${v.referrer}` : "direct"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminInsights;